import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import './VerifyEmail.css';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. Please check your email.');
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/auth/verify-email/${token}`,
        );

        setStatus('success');
        setMessage(
          response.data.message ||
            'Email verified successfully! You can now log in.',
        );

        // Update user data in localStorage if user is logged in
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            user.email_verified = true;
            localStorage.setItem('user', JSON.stringify(user));

            // Trigger a storage event to update other components
            window.dispatchEvent(new Event('storage'));
          } catch (e) {
            console.error('Failed to update user data in localStorage:', e);
          }
        }

        // Redirect to dashboard or login after 3 seconds
        setTimeout(() => {
          const isLoggedIn = localStorage.getItem('token');
          navigate(isLoggedIn ? '/dashboard' : '/');
        }, 3000);
      } catch (err) {
        setStatus('error');
        setMessage(
          err.response?.data?.error ||
            'Email verification failed. The link may be invalid or expired.',
        );
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResending(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/resend-verification`,
        { email: resendEmail },
      );

      alert(
        response.data.message ||
          'Verification email sent! Please check your inbox.',
      );
      setResendEmail('');
    } catch (err) {
      alert(
        err.response?.data?.error || 'Failed to resend verification email.',
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="verify-email-container">
      <div className="verify-email-card">
        {status === 'verifying' && (
          <>
            <div className="spinner"></div>
            <h2>Verifying your email...</h2>
            <p>Please wait while we verify your email address.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="success-icon">✓</div>
            <h2>Email Verified!</h2>
            <p>{message}</p>
            <p className="redirect-message">
              Redirecting you to the dashboard in a few seconds...
            </p>
            <Link to="/dashboard" className="login-link">
              Go to Dashboard
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="error-icon">✗</div>
            <h2>Verification Failed</h2>
            <p>{message}</p>

            <div className="resend-section">
              <h3>Need a new verification link?</h3>
              <form onSubmit={handleResendVerification}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  required
                  disabled={resending}
                />
                <button type="submit" disabled={resending}>
                  {resending ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </form>
            </div>

            <Link to="/" className="login-link">
              Back to Login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
